// The CFO view (CFO plan 5.3–5.5): one screen, top to bottom — cash, the 12-week forecast, profit live, break-even,
// utilization, work in progress, job health, internal projects, budgets, the tax clock, lead sources. Pure, so the web
// page, the phone screen and the MCP show the same numbers.
//
// The books publish what's on the ledger (opsCfo/now, owners only); what the work has earned, the hours and what
// they cost are worked out here from the clock, the tasks and pay. Every figure carries its basis for the chip:
//   booked  — on the books (or clocked, for hours)
//   earned  — worked out from the work: % complete × contract, hours × rate
//   shadow  — includes unpaid time at shadow pay: never posted, "not booked"
// and a `src` the screens turn into a link (cfoSourceLink).

import { localDateStr } from './time.js';
import { canonTag, resolveTags, tagIndex as tagIndexOf } from './tags.js';
import { isProjectLive } from './projectstatus.js';
import { computeProjectCosting } from './costing.js';
import { receiptLines } from './metrics.js';
import {
  BILLABLE_JOB_TYPES, jobTypeOf, dayMoney, estimateProgress, jobCostsOf, contractValueOf, personRates, overheadRate,
  forecastJob, jobPaysFor, moneyShort, agentUids,
} from './profit.js';
import { budgetActuals, forwardOverhead } from './budgets.js';
import { overheadTrust } from './trust.js';

const H = 3600000;
const DAY = 86400000;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const round2 = (v) => Math.round(v * 100) / 100;
const round1 = (v) => Math.round(v * 10) / 10;
const per = (a, b) => (b > 0 ? round2(a / b) : null);

/** The second benchmark for what internal hours could have earned: Lj's shadow pay. */
export const SHADOW_BENCHMARK = 41.5;
export const CFO_SECTIONS = ['cash', 'forecast', 'profit', 'breakEven', 'utilization', 'wip', 'jobHealth', 'internal', 'budgets', 'compliance', 'leads'];
export const BASIS_LABELS = { booked: 'booked', earned: 'earned', shadow: 'incl. shadow' };

/** A figure with its basis (and where it comes from). */
export const fig = (value, basis, src = null) => ({ value: value == null ? null : round2(value), basis, ...(src ? { src } : {}) });

const BOOKS = 'https://app.battlebooks.ca';
/**
 * Where a figure comes from → { href, external, label }. 'books:<page>' opens BattleBooks; 'job:<books id>' the books
 * job; 'project:<ops id>' the project here; 'setup:money' Setup → Money.
 */
export function cfoSourceLink(src) {
  if (!src) return null;
  const [kind, id] = String(src).split(/:(.*)/s);
  const books = {
    bank: ['/accounting/bank/', 'Bank'], ar: ['/reports/ar-aging/', 'AR aging'], ap: ['/accounting/ap-aging/', 'AP aging'],
    gst: ['/accounting/gst-report/', 'GST report'], ledger: ['/accounting/ledger/', 'Ledger'], pnl: ['/accounting/pnl/', 'Profit & loss'],
    compliance: ['/accounting/compliance/', 'Compliance'], payroll: ['/payroll/', 'Payroll'], recurring: ['/accounting/recurring/', 'Recurring'],
    cashflow: ['/reports/cash-flow/', 'Cash flow'], invoices: ['/invoices/', 'Invoices'], projects: ['/projects/', 'Jobs'], margin: ['/accounting/project-margin/', 'Job margin'],
    coa: ['/accounting/chart-of-accounts/', 'Chart of accounts'], settings: ['/settings/', 'Settings'],
  };
  if (kind === 'books' && books[id]) return { href: `${BOOKS}${books[id][0]}`, external: true, label: books[id][1] };
  if (kind === 'job' && id) return { href: `${BOOKS}/projects/view/?id=${encodeURIComponent(id)}`, external: true, label: 'Job in BattleBooks' };
  if (kind === 'project' && id) return { href: `/projects/${id}`, external: false, label: 'Project', projectId: id };
  if (kind === 'setup' && id === 'money') return { href: '/settings/money', external: false, label: 'Setup → Money' };
  if (kind === 'team') return { href: '/team', external: false, label: 'Team' };
  return null;
}

// ── windows of work ───────────────────────────────────────────────────────────────────────────────────────────────

const isWork = (s, agents) => s && s.startedAt && !s.overlay && s.type !== 'travel' && !s.deletedAt && !agents.has(s.ownerUid);
const clip = (s, from, to, now) => {
  const a = Math.max(num(s.startedAt), from);
  const b = Math.min(s.endedAt ? num(s.endedAt) : now, to);
  return b > a ? b - a : 0;
};
/** Monday 00:00 (local) of the week holding `now`. */
export function weekStartMs(now = Date.now()) {
  const d = new Date(now); d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d.getTime();
}
export const monthStartMs = (now = Date.now()) => { const d = new Date(now); return new Date(d.getFullYear(), d.getMonth(), 1).getTime(); };

function indexes({ projects = [], jobs = [], departments = [] }) {
  const projById = new Map(projects.map((p) => [p.id, p]));
  const jobById = new Map(jobs.map((j) => [j.id, j]));
  const deptById = new Map(departments.map((d) => [d.id, d]));
  const typeOf = (pid) => {
    const p = projById.get(pid) || { id: pid };
    return jobTypeOf(p, p.bookProjectId ? jobById.get(p.bookProjectId) || null : null, deptById.get(p.departmentId) || null);
  };
  return { projById, jobById, deptById, typeOf };
}

/**
 * Billable work in a window: what it earned (fixed: contract × estimate finished in the window ÷ the whole estimate;
 * hourly: hours × rate — dayMoney's rules), its labour both ways, and its job costs (the job's costs to date × the
 * share of its progress that fell in the window). → { hours, earned, jobCosts, labour, contribution, perHour, rows }
 */
export function windowMoney({ sessions = [], tasks = [], projects = [], jobs = [], departments = [], pay = {}, expenses = [], agents = new Set(), me = null, from, to, now = Date.now() }) {
  const ix = indexes({ projects, jobs, departments });
  const work = []; const allMs = new Map();
  for (const s of sessions) {
    if (!isWork(s, agents) || !s.projectId) continue;
    allMs.set(s.projectId, (allMs.get(s.projectId) || 0) + clip(s, 0, now, now));
    const ms = clip(s, from, to, now);
    if (ms > 0) work.push({ projectId: s.projectId, ownerUid: s.ownerUid || me, ms });
  }
  const inWin = (ms) => num(ms) >= from && num(ms) < to;
  const dm = dayMoney({ work, tasks, projects, jobs, departments, pay, me, inDay: inWin });
  const rows = dm.rows.filter((r) => BILLABLE_JOB_TYPES.has(r.type)).map((r) => {
    const p = ix.projById.get(r.projectId) || { id: r.projectId };
    const job = p.bookProjectId ? ix.jobById.get(p.bookProjectId) || null : null;
    const jc = jobCostsOf(job) ?? round2(expenses.filter((e) => e.projectId === r.projectId && e.type !== 'income').reduce((a, e) => a + num(e.amount), 0));
    let share = 0;
    if (r.type === 'fixed') {
      const mine = tasks.filter((t) => t.projectId === r.projectId && !t.bucket);
      const all = estimateProgress(mine);
      const win = estimateProgress(mine, (t) => t.status === 'done' && inWin(t.completedAt));
      share = all.doneMin > 0 ? win.doneMin / all.doneMin : 0;
    } else {
      const tot = (allMs.get(r.projectId) || 0) / H;
      share = tot > 0 ? Math.min(1, r.hours / tot) : 0;
    }
    return { ...r, jobCosts: round2(num(jc) * share) };
  });
  const sum = (f) => round2(rows.reduce((a, r) => a + f(r), 0));
  const hours = sum((r) => r.hours); const earned = sum((r) => r.earned); const jobCosts = sum((r) => r.jobCosts);
  const labour = { actual: sum((r) => r.labour.actual), paidOut: sum((r) => r.labour.paidOut) };
  const contribution = { actual: round2(earned - jobCosts - labour.actual), paidOut: round2(earned - jobCosts - labour.paidOut) };
  return {
    from, to, hours, earned, jobCosts, labour, contribution,
    perHour: { earned: per(earned, hours), contributionActual: per(contribution.actual, hours), contributionPaidOut: per(contribution.paidOut, hours), cost: per(jobCosts + labour.actual, hours) },
    rows,
  };
}

/** Billable and clocked hours per person and for the company in [from, to). → { company, people: [...] } */
export function utilization({ sessions = [], projects = [], jobs = [], departments = [], agents = new Set(), me = null, from, to, now = Date.now() }) {
  const ix = indexes({ projects, jobs, departments });
  const by = new Map();
  for (const s of sessions) {
    if (!isWork(s, agents)) continue;
    const ms = clip(s, from, to, now);
    if (!ms) continue;
    const uid = s.ownerUid || me || '_me';
    const o = by.get(uid) || { uid, clocked: 0, billable: 0 };
    o.clocked += ms;
    if (s.projectId && BILLABLE_JOB_TYPES.has(ix.typeOf(s.projectId))) o.billable += ms;
    by.set(uid, o);
  }
  const people = [...by.values()].map((o) => ({ uid: o.uid, clocked: round2(o.clocked / H), billable: round2(o.billable / H), pct: o.clocked > 0 ? Math.round((o.billable / o.clocked) * 100) : null }))
    .sort((a, b) => b.clocked - a.clocked);
  const clocked = round2(people.reduce((a, p) => a + p.clocked, 0)); const billable = round2(people.reduce((a, p) => a + p.billable, 0));
  return { company: { clocked, billable, pct: clocked > 0 ? Math.round((billable / clocked) * 100) : null }, people };
}

// ── internal projects (5.4) ───────────────────────────────────────────────────────────────────────────────────────

/**
 * Internal projects on real cost vs what the hours could have earned. Per project, for this week, this month and to
 * date: hours; cash cost = books expenses on the project or its tags (field receipts, dated; to date at least the
 * books job's costs) + wages of the paid people's hours (loaded); unpaid time = hours × shadow (shown, not booked);
 * could have earned = hours × the 90-day earned per billable hour, and × $41.50 as the second benchmark. Before tax.
 */
export function internalView({ projects = [], sessions = [], tasks = [], jobs = [], departments = [], pay = {}, receipts = [], expenses = [], budgets = [], tagIndex = null, agents = new Set(), earnedPerHour = null, benchmark = SHADOW_BENCHMARK, me = null, now = Date.now() }) {
  const ix = indexes({ projects, jobs, departments });
  const week = weekStartMs(now); const month = monthStartMs(now);
  const wins = { week: [week, now], month: [month, now], toDate: [0, now] };
  const lines = receiptLines(receipts, projects);
  const tagsOf = (p) => (tagIndex ? resolveTags(p.tags || [], tagIndex) : p.tags || []).map(canonTag).filter(Boolean);
  const out = [];
  for (const p of projects) {
    if (!p?.id || ix.typeOf(p.id) !== 'internal') continue;
    const mine = sessions.filter((s) => s.projectId === p.id && isWork(s, agents));
    const tags = new Set(tagsOf(p));
    const myLines = lines.filter((l) => l.projectId === p.id || (l.tags || []).map(canonTag).some((t) => tags.has(t)));
    const myExp = expenses.filter((e) => e.projectId === p.id && e.type !== 'income');
    const job = p.bookProjectId ? ix.jobById.get(p.bookProjectId) || null : null;
    const periods = {};
    for (const [k, [from, to]] of Object.entries(wins)) {
      const byUid = new Map();
      for (const s of mine) { const ms = clip(s, from, to, now); if (ms) byUid.set(s.ownerUid || me, (byUid.get(s.ownerUid || me) || 0) + ms); }
      let hours = 0; let wages = 0; let unpaidH = 0; let unpaid = 0;
      for (const [uid, ms] of byUid) {
        const h = ms / H; const r = personRates(pay[uid] || null);
        hours += h;
        if (r.actual > 0) wages += h * r.actual;
        else { unpaidH += h; unpaid += h * num(r.paidOut); }
      }
      const inRange = (d) => d && (k === 'toDate' || (d >= localDateStr(from) && d <= localDateStr(to)));
      let spent = myLines.filter((l) => inRange(l.date)).reduce((a, l) => a + num(l.amount), 0)
        + myExp.filter((e) => inRange(typeof e.date === 'string' ? e.date : e.createdAt ? localDateStr(e.createdAt) : null)).reduce((a, e) => a + num(e.amount), 0);
      if (k === 'toDate') spent = Math.max(spent, num(jobCostsOf(job)), num(periods.month?.expenses?.value));   // to date is never less than this month
      if (k === 'month') {   // a money budget on this project for the month carries the ledger's figure
        const b = budgets.find((x) => x.measure === 'money' && x.period === 'month' && x.target?.kind === 'project' && x.target.id === p.id && x.actual != null);
        if (b) spent = Math.max(spent, num(b.actual));
      }
      const cash = spent + wages;
      periods[k] = {
        hours: round2(hours),
        cash: fig(cash, 'booked'), expenses: fig(spent, 'booked'), wages: fig(wages, 'booked'),
        unpaid: { hours: round2(unpaidH), value: fig(unpaid, 'shadow') },
        couldEarn: earnedPerHour != null ? fig(hours * earnedPerHour, 'earned') : null,
        benchmark: fig(hours * benchmark, 'shadow'),
      };
    }
    const lines2 = budgets.filter((b) => b.target?.kind === 'project' && b.target.id === p.id);
    if (!periods.toDate.hours && !periods.toDate.cash.value && !lines2.length && !isProjectLive(p)) continue;
    out.push({ projectId: p.id, name: p.name || 'Project', periods, budgets: lines2, sentence: tradeOff(p.name || 'It', periods.week, earnedPerHour), src: `project:${p.id}` });
  }
  out.sort((a, b) => b.periods.month.hours - a.periods.month.hours || b.periods.toDate.hours - a.periods.toDate.hours);
  return { rows: out, earnedPerHour, benchmark };
}

/** "This week BattleOps took 18 h = $150 cash; the same hours on billable work would have brought in ~$3,600." */
export function tradeOff(name, w, earnedPerHour) {
  if (!w || !(w.hours > 0)) return `${name}: no hours this week.`;
  const z = earnedPerHour != null ? w.couldEarn?.value : w.benchmark?.value;
  const unpaid = w.unpaid?.hours > 0 ? ` (plus ${round1(w.unpaid.hours)} h of unpaid time, ${moneyShort(w.unpaid.value.value)} at shadow pay, not booked)` : '';
  return `This week ${name} took ${round1(w.hours)} h = ${moneyShort(w.cash.value)} cash${unpaid}; the same hours on billable work would have brought in ~${moneyShort(z)}.`;
}

// ── leads (5.5, the BattleOps half) ───────────────────────────────────────────────────────────────────────────────

/**
 * The books' lead sources and customers with BattleOps' half added: hours on each source's / customer's jobs (books job
 * ids → projects here by bookProjectId), marketing hours (time on projects or tasks tagged with the channel), callbacks
 * (done tasks tagged `callback` on those jobs), and profit per hour.
 */
export function leadsView({ leads = null, projects = [], sessions = [], tasks = [], tagIndex = null, agents = new Set(), now = Date.now() }) {
  if (!leads) return null;
  const opsOf = new Map();
  for (const p of projects) if (p?.bookProjectId) opsOf.set(p.bookProjectId, [...(opsOf.get(p.bookProjectId) || []), p.id]);
  const msBy = new Map();
  for (const s of sessions) if (isWork(s, agents) && s.projectId) msBy.set(s.projectId, (msBy.get(s.projectId) || 0) + clip(s, 0, now, now));
  const tagsOf = (x) => (tagIndex ? resolveTags(x?.tags || [], tagIndex) : x?.tags || []).map(canonTag);
  const projTags = new Map(projects.map((p) => [p.id, tagsOf(p)]));
  const callbacksOn = new Map();
  for (const t of tasks) if (t?.status === 'done' && t.projectId && tagsOf(t).includes('callback')) callbacksOn.set(t.projectId, (callbacksOn.get(t.projectId) || 0) + 1);
  const opsIds = (jobIds = []) => jobIds.flatMap((j) => opsOf.get(j) || []);
  const hoursOn = (ids) => round2(ids.reduce((a, id) => a + (msBy.get(id) || 0), 0) / H);
  const callbacks = (ids) => ids.reduce((a, id) => a + (callbacksOn.get(id) || 0), 0);
  const marketing = (slug) => {
    if (!slug || slug === 'unknown') return 0;
    let ms = 0;
    for (const p of projects) if ((projTags.get(p.id) || []).includes(slug)) ms += msBy.get(p.id) || 0;
    for (const t of tasks) {
      if (!t || (t.projectId && (projTags.get(t.projectId) || []).includes(slug)) || !tagsOf(t).includes(slug)) continue;
      for (const e of t.timeEntries || []) ms += num(e.duration);
    }
    return round2(ms / H);
  };
  const add = (r) => {
    const ids = opsIds(r.jobIds);
    const hours = hoursOn(ids);
    return { ...r, projectIds: ids, hours, callbacks: callbacks(ids), profitPerHour: per(num(r.profit), hours), basis: 'booked' };
  };
  return {
    bySource: (leads.bySource || []).map((r) => { const x = add(r); const mh = marketing(r.source); return { ...x, marketingHours: mh, profitPerAllHours: per(num(r.profit), x.hours + mh) }; }),
    byCustomer: (leads.byCustomer || []).map(add),
  };
}

// ── the whole view ────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Everything the CFO view shows, in order. `cfo` = opsCfo/now (null until the books publish it); `rates` =
 * opsRates/overhead; `pay` = { [uid]: opsPay }; `members` (opsMembers — `agent: true` time never counts as hours);
 * `tags` = opsTags docs (or an index); `settings` = meta/settings (cfo.plannedBillableHours a month).
 */
export function cfoView({
  cfo = null, rates = null, sessions = [], projects = [], jobs = [], pay = {}, budgets = null, metrics = [], metricValues = [],
  metricCtx = null, departments = [], tasks = [], members = [], expenses = [], receipts = [], jobPays = [], tags = [], settings = null,
  me = null, now = Date.now(),
} = {}) {
  const agents = agentUids(members);
  const work = sessions.filter((s) => isWork(s, agents) || s?.type === 'travel');
  const tagIndex = tagIndexOf(tags);
  const today = localDateStr(now);
  const base = { sessions: work, tasks, projects, jobs, departments, pay, expenses, agents, me, now };
  const policy = rates?.policy || null;
  const target = policy?.targetMarginPct ?? null;

  // the 90-day window (the overhead pool's), this week, this month
  const from90 = num(rates?.window?.from) || now - 90 * DAY;
  const w90 = windowMoney({ ...base, from: from90, to: now });
  const wk = weekStartMs(now); const mo = monthStartMs(now);
  const rate = overheadRate({ rates, sessions: work, projects, jobs, departments, pay, me, now });

  // 1. cash (booked)
  const c = cfo?.cash || null;
  const cash = c ? {
    basis: 'booked',
    bank: fig(c.bank, 'booked', 'books:bank'), banks: c.banks || [],
    ar: fig(c.ar?.total, 'booked', 'books:ar'), arOverdue: fig(c.ar?.overdue, 'booked', 'books:ar'), aging: c.ar?.aging || null,
    ap: fig(c.ap, 'booked', 'books:ap'), gstOwing: fig(c.gstOwing, 'booked', 'books:gst'), gstPeriods: c.gstPeriods || [],
    dueToOfficer: fig(c.dueToOfficer, 'booked', 'books:ledger'), payrollPayable: fig(c.payrollPayable, 'booked', 'books:payroll'),
    deposits: fig(c.deposits, 'booked', 'books:ledger'),
    runwayMonths: c.runwayMonths == null ? null : round1(c.runwayMonths), avgMonthlySpend: fig(c.avgMonthlySpend, 'booked', 'books:pnl'),
  } : null;

  // 2. forecast (booked + billing stages)
  const f = cfo?.forecast || null;
  const forecast = f ? { ...f, basis: 'booked', src: 'books:cashflow', dip: f.firstNegativeWeek ? (f.weeks || []).find((w) => w.week === f.firstNegativeWeek) || null : null } : null;

  // 3. profit, live: this month earned (incl. % complete) — actual and if paid out — vs the books' months
  const m = windowMoney({ ...base, from: mo, to: now });
  const bookedNow = cfo?.profit?.thisMonth || null;
  const ohMonth = num(bookedNow?.overhead);
  const ohMonthPaid = overheadRate({ rates: { pool: ohMonth, window: { from: mo, to: now } }, sessions: work, projects, jobs, departments, pay, me, now });
  const ohPaid = ohMonthPaid ? num(ohMonthPaid.poolPaidOut) : ohMonth;
  const gross = { actual: m.earned - m.jobCosts - m.labour.actual, paidOut: m.earned - m.jobCosts - m.labour.paidOut };
  const profit = {
    month: localDateStr(mo).slice(0, 7),
    earned: {
      revenue: fig(m.earned, 'earned'), jobCosts: fig(m.jobCosts, 'booked'),
      labour: { actual: fig(m.labour.actual, 'booked'), paidOut: fig(m.labour.paidOut, 'shadow') },
      grossMargin: { actual: fig(gross.actual, 'earned'), paidOut: fig(gross.paidOut, 'shadow') },
      overhead: { actual: fig(ohMonth, 'booked', 'books:pnl'), paidOut: fig(ohPaid, 'shadow') },
      net: { actual: fig(gross.actual - ohMonth, 'earned'), paidOut: fig(gross.paidOut - ohPaid, 'shadow') },
      hours: m.hours,
    },
    booked: ['thisMonth', 'lastMonth', 'lastYearSameMonth'].map((k) => {
      const p = cfo?.profit?.[k];
      return p ? { key: k, period: p.period, basis: 'booked', src: 'books:pnl', revenue: fig(p.revenue, 'booked'), cogs: fig(p.cogs, 'booked'), grossMargin: fig(p.grossMargin, 'booked'), grossMarginPct: p.grossMarginPct ?? null, overhead: fig(p.overhead, 'booked'), net: fig(p.net, 'booked') } : null;
    }).filter(Boolean),
  };

  // 4. break-even: weekly overhead ÷ profit per billable hour (90 days, before overhead); this week's billable hours
  const days = num(rates?.window?.days) || Math.max(1, Math.round((now - from90) / DAY));
  const weeklyOh = rates?.pool != null ? (num(rates.pool) / days) * 7 : null;
  const weeklyOhPaid = rate ? (num(rate.poolPaidOut) / days) * 7 : weeklyOh;
  const util90 = utilization({ ...base, from: from90, to: now });
  const utilWeek = utilization({ ...base, from: wk, to: now });
  const plannedHours = num(settings?.cfo?.plannedBillableHours) || null;
  const budgetRows = budgets || budgetActuals(cfo?.budgets || [], { ...base, sessions: work, tagIndex, metrics, metricValues, metricCtx: metricCtx || { sessions: work, tasks, projects, jobs, receipts, me, now } }, today);
  const fwd = forwardOverhead(budgetRows, { tagIndex, plannedHours });
  const need = (oh, ph) => (oh != null && ph != null && ph > 0 ? round1(oh / ph) : null);
  const breakEven = {
    weeklyOverhead: { actual: fig(weeklyOh, 'booked', 'setup:money'), paidOut: fig(weeklyOhPaid, 'shadow', 'setup:money') },
    perHour: { actual: fig(w90.perHour.contributionActual, 'earned'), paidOut: fig(w90.perHour.contributionPaidOut, 'shadow') },
    hoursNeeded: { actual: need(weeklyOh, w90.perHour.contributionActual), paidOut: need(weeklyOhPaid, w90.perHour.contributionPaidOut) },
    thisWeek: { billable: utilWeek.company.billable, clocked: utilWeek.company.clocked, basis: 'booked' },
    overheadRate: rate ? { actual: fig(rate.actual, 'booked', 'setup:money'), paidOut: fig(rate.paidOut, 'shadow', 'setup:money'), billableHours: rate.billableHours } : null,
    forward: { ...fwd, perHour: fig(fwd.perHour, 'booked') },
    window: { from: from90, to: now, hours: w90.hours, earned: fig(w90.earned, 'earned'), jobCosts: fig(w90.jobCosts, 'booked'), labour: w90.labour },
  };

  // 5. utilization (90 days + this week); earned per billable hour vs the rate the target margin needs
  const costPerHour = w90.perHour.cost != null ? w90.perHour.cost + num(rate?.actual) : null;
  const targetRate = target != null && costPerHour != null && target < 100 ? round2(costPerHour / (1 - target / 100)) : null;
  const utilizationOut = {
    basis: 'booked', window: { from: from90, to: now }, company: util90.company, people: util90.people, week: utilWeek.company,
    earnedPerHour: fig(w90.perHour.earned, 'earned'), costPerHour: fig(costPerHour, 'booked'), targetMarginPct: target, targetRate: fig(targetRate, 'earned'),
  };

  // 6. work in progress (the books' WIP for this month)
  const wip = cfo?.wip ? { ...cfo.wip, basis: 'earned', src: 'books:margin', unbilled: fig(cfo.wip.unbilled, 'earned', 'books:invoices'), inExcess: fig(cfo.wip.inExcess, 'booked', 'books:invoices') } : null;

  // 7. job health: each open billable job's margin at completion (forecastJob), below the target margin flagged
  const ix = indexes({ projects, jobs, departments });
  const jobHealth = [];
  for (const p of projects) {
    if (!p?.id || !isProjectLive(p)) continue;
    const type = ix.typeOf(p.id);
    if (!BILLABLE_JOB_TYPES.has(type)) continue;
    const job = p.bookProjectId ? ix.jobById.get(p.bookProjectId) || null : null;
    const costing = computeProjectCosting({ project: p, tasks, sessions: work, expenses, pay, job, dept: ix.deptById.get(p.departmentId) || null, me, now, overhead: rate, jobPays, policy });
    const onJob = jobPaysFor(jobPays, p.id);
    const fj = forecastJob({ cfo: costing.cfo, contract: contractValueOf(p, job), tasks: tasks.filter((t) => t.projectId === p.id && !t.bucket), ratesOf: (uid) => personRates(pay[uid] || null, onJob[uid] || null, policy), me });
    if (!fj) continue;
    const pct = costing.cfo?.revenue?.pct;
    jobHealth.push({
      projectId: p.id, name: p.name || 'Project', type, bookProjectId: p.bookProjectId || null, src: `project:${p.id}`,
      pctComplete: pct == null ? null : Math.round(pct * 100), revenue: fig(fj.revenue, 'earned'), remainingHours: fj.remainingHours,
      profit: { actual: fig(fj.profit.actual, 'earned'), paidOut: fig(fj.profit.paidOut, 'shadow') },
      marginPct: fj.marginPct, below: target != null && fj.marginPct != null && fj.marginPct.actual < target, noRevenue: !(fj.revenue > 0),
    });
  }
  jobHealth.sort((a, b) => (a.marginPct?.actual ?? -Infinity) - (b.marginPct?.actual ?? -Infinity));

  // 8. internal projects
  const benchmark = num(pay?.[me]?.shadowRatePerHour) || SHADOW_BENCHMARK;
  const internal = internalView({ ...base, receipts, budgets: budgetRows, tagIndex, earnedPerHour: w90.perHour.earned, benchmark });

  // 10. tax + compliance clock
  const compliance = cfo ? {
    basis: 'booked', src: 'books:compliance',
    rows: (cfo.compliance || []).map((r) => ({ ...r, amount: r.amount == null ? null : fig(r.amount, 'booked') })),
    taxReserve: cfo.taxReserve || null,
  } : null;

  // 11. lead sources + customers
  const leads = leadsView({ leads: cfo?.leads || null, projects, sessions: work, tasks, tagIndex, agents, now });

  // what the numbers are missing (the trust strip; the setup check joins it later)
  const trust = [];
  if (!cfo) trust.push({ key: 'cfo:none', text: 'The books haven’t published the CFO view yet, so cash, the forecast and budgets are empty', fix: 'books' });
  else if (cfo.publishedAt && now - num(cfo.publishedAt) > 2 * DAY) trust.push({ key: 'cfo:stale', text: `The books last published ${Math.round((now - num(cfo.publishedAt)) / DAY)} days ago`, fix: 'books' });
  trust.push(...overheadTrust(rates));
  if (f?.undated?.length) trust.push({ key: 'cfo:undated', text: `${f.undated.length} billing stage${f.undated.length === 1 ? ' has' : 's have'} no date, so the forecast leaves ${moneyShort(f.undated.reduce((a, u) => a + num(u.amount), 0))} out`, fix: 'stages' });
  const noPay = util90.people.filter((x) => x.billable > 0 && personRates(pay[x.uid] || null).missing);
  if (noPay.length) trust.push({ key: 'cfo:pay', text: `${noPay.length} ${noPay.length === 1 ? 'person has' : 'people have'} billable hours but no pay set, so labour reads $0`, fix: 'pay' });
  if (target == null) trust.push({ key: 'cfo:target', text: 'No target margin in BattleBooks payroll settings, so job health and the target rate can’t be judged', fix: 'books' });

  return {
    today, publishedAt: cfo?.publishedAt || null, ready: !!cfo, trust,
    cash, forecast, profit, breakEven, utilization: utilizationOut, wip, jobHealth, internal, budgets: budgetRows, compliance, leads,
  };
}

/** One section of the view by name (MCP `ops_cfo section`). */
export const cfoSection = (view, name) => (CFO_SECTIONS.includes(name) ? view?.[name] ?? null : null);

/** Days from today to a YYYY-MM-DD date (negative = past). */
export const daysUntil = (date, today) => (date && today ? Math.round((new Date(`${date}T12:00:00`) - new Date(`${today}T12:00:00`)) / DAY) : null);
